import * as THREE from 'three/webgpu';
import type { FeatureFace } from '../../mesh/feature';
import type { Viewer } from '../../viewer/Viewer';
import { queryClosestPointOnlyOnFeatureFaceMeshViaBvh } from '../../mesh/feature/acceleration/featureFaceSpatialQuery';
import {
    areMeasureNormalsParallel,
    resolveBoundedPlanarFaceWorldPlane,
} from '../utils/measureBoundedPlanarFace';
import {
    collectFeatureFaceWorldVertices,
    computeClosestPointsBetweenMeshFaces,
} from '../utils/measureMeshFaceClosestPoints';
import type { MeasureEntityRef } from '../types/measure-types';
import {
    MeasureDistancePrimitiveKind,
    type MeasureClosestPointResult,
    type MeasureDistanceGeometry,
} from '../types/measure-distance-types';
import { MeasureEntityGeometryAdapter } from './MeasureEntityGeometryAdapter';
import { computeDistanceBetweenPointsMillimeter } from '../math/MeasureNumericKernel';

/** 几何退化判定容差（场景世界单位，米制坐标；用于线段长度、平行性等数值比较）。 */
const MEASURE_CLOSEST_DISTANCE_EPSILON = 1e-9;

/** 判定退化距离与“不绘制测量线”的容差（毫米）。 */
const MEASURE_CLOSEST_DISTANCE_EPSILON_MM = 0.0001;

/**
 * 两测量实体最近点求解器。
 *
 * 职责：在默认测量模式（非 Point-to-Point）下，计算两选中几何体之间的全局最短距离点对。
 */
export class MeasureClosestPointResolver {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** 几何适配器。 */
    private readonly geometryAdapter: MeasureEntityGeometryAdapter;

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
        this.geometryAdapter = new MeasureEntityGeometryAdapter(viewer);
    }

    /**
     * 求解两测量实体之间的最近点。
     *
     * @param firstEntity 先选实体（最近点 `from` 落在该实体上）。
     * @param secondEntity 后选实体（最近点 `to` 落在该实体上）。
     * @returns 最近点结果；无法求解时返回 null。
     */
    public resolve(
        firstEntity: MeasureEntityRef,
        secondEntity: MeasureEntityRef,
    ): MeasureClosestPointResult | null {
        const geometryA = this.geometryAdapter.adapt(firstEntity);
        const geometryB = this.geometryAdapter.adapt(secondEntity);
        if (!geometryA || !geometryB) {
            return null;
        }

        const pair = solveClosestPointsBetweenGeometries(geometryA, geometryB);
        if (!pair) {
            return null;
        }

        const distance = computeDistanceBetweenPointsMillimeter(pair.pointA, pair.pointB);
        const shouldRenderMeasureLines = distance > MEASURE_CLOSEST_DISTANCE_EPSILON_MM;

        return {
            from: pair.pointA.clone(),
            to: pair.pointB.clone(),
            distance,
            shouldRenderMeasureLines,
        };
    }

    /**
     * 求世界坐标点到测量实体几何体上的最近点。
     *
     * 设计说明：供圆弧/圆「中心到中心」混合几何体分支复用，避免重复适配逻辑。
     *
     * @param entityRef 测量实体引用。
     * @param worldPoint 世界坐标查询点。
     * @returns 实体上的最近点；无法求解时返回 null。
     */
    public resolveClosestPointOnEntityFromWorldPoint(
        entityRef: MeasureEntityRef,
        worldPoint: THREE.Vector3,
    ): THREE.Vector3 | null {
        const geometry = this.geometryAdapter.adapt(entityRef);
        if (!geometry) {
            return null;
        }

        const pair = closestPointToGeometry(worldPoint, geometry);
        return pair?.pointB ?? null;
    }

    /**
     * 求世界坐标点与测量实体之间的最近点参考点对。
     *
     * @param worldPoint 世界坐标点。
     * @param entityRef 目标测量实体。
     * @param pointSideIsFirst 世界坐标点是否对应先选实体侧。
     * @returns 最近点结果；无法求解时返回 null。
     */
    public resolveBetweenWorldPointAndEntity(
        worldPoint: THREE.Vector3,
        entityRef: MeasureEntityRef,
        pointSideIsFirst: boolean,
    ): MeasureClosestPointResult | null {
        const pointGeometry: MeasureDistanceGeometry = {
            kind: MeasureDistancePrimitiveKind.Point,
            position: worldPoint.clone(),
        };
        const entityGeometry = this.geometryAdapter.adapt(entityRef);
        if (!entityGeometry) {
            return null;
        }

        const geometryA = pointSideIsFirst ? pointGeometry : entityGeometry;
        const geometryB = pointSideIsFirst ? entityGeometry : pointGeometry;
        const pair = solveClosestPointsBetweenGeometries(geometryA, geometryB);
        if (!pair) {
            return null;
        }

        const distance = computeDistanceBetweenPointsMillimeter(pair.pointA, pair.pointB);
        return {
            from: pair.pointA.clone(),
            to: pair.pointB.clone(),
            distance,
            shouldRenderMeasureLines: distance > MEASURE_CLOSEST_DISTANCE_EPSILON_MM,
        };
    }
}

/**
 * 两求距几何体之间的最近点对。
 */
interface ClosestPointPair {
    /** 几何体 A 上的最近点。 */
    pointA: THREE.Vector3;
    /** 几何体 B 上的最近点。 */
    pointB: THREE.Vector3;
}

/**
 * 测量链路专用的点到网格面最近点查询。
 *
 * 设计说明：
 * 1. 测量线只需要最近点，不依赖曲面法向；
 * 2. torus/cone 等复杂曲面的局部三角法向可能退化，不能因此丢弃有效最近点；
 * 3. 需要法向的参考几何功能仍使用 `projectPointOnFeatureSurface`，避免改变既有语义。
 *
 * @param face 面代理。
 * @param point 查询点（世界坐标）。
 * @param maxThreshold 可选最大搜索距离。
 * @returns 面上的最近点；无法查询时返回 null。
 */
function closestPointOnMeshFaceForMeasure(
    face: FeatureFace,
    point: THREE.Vector3,
    maxThreshold?: number,
): THREE.Vector3 | null {
    const projection = queryClosestPointOnlyOnFeatureFaceMeshViaBvh(face, point, { maxThreshold });
    return projection?.point.clone() ?? null;
}

/**
 * 求解两几何体之间的最近点对。
 *
 * @param geometryA 几何体 A。
 * @param geometryB 几何体 B。
 * @returns 最近点对；无法求解时返回 null。
 */
function solveClosestPointsBetweenGeometries(
    geometryA: MeasureDistanceGeometry,
    geometryB: MeasureDistanceGeometry,
): ClosestPointPair | null {
    const rankA = primitiveRank(geometryA.kind);
    const rankB = primitiveRank(geometryB.kind);
    if (rankA <= rankB) {
        return dispatchClosestPoints(geometryA, geometryB);
    }
    const reversed = dispatchClosestPoints(geometryB, geometryA);
    if (!reversed) {
        return null;
    }
    return {
        pointA: reversed.pointB,
        pointB: reversed.pointA,
    };
}

/**
 * 基元优先级（数值越小越优先作为 dispatch 左侧）。
 *
 * @param kind 基元种类。
 * @returns 优先级。
 */
function primitiveRank(kind: MeasureDistancePrimitiveKind): number {
    switch (kind) {
        case MeasureDistancePrimitiveKind.Point:
            return 0;
        case MeasureDistancePrimitiveKind.Segment:
            return 1;
        case MeasureDistancePrimitiveKind.InfiniteLine:
            return 2;
        case MeasureDistancePrimitiveKind.Polyline:
            return 3;
        case MeasureDistancePrimitiveKind.Plane:
            return 4;
        case MeasureDistancePrimitiveKind.MeshFace:
            return 5;
        default:
            return 99;
    }
}

/**
 * 按左侧基元种类分派最近点求解。
 *
 * @param geometryA 几何体 A。
 * @param geometryB 几何体 B。
 * @returns 最近点对（点 A 在 geometryA 上，点 B 在 geometryB 上）。
 */
function dispatchClosestPoints(
    geometryA: MeasureDistanceGeometry,
    geometryB: MeasureDistanceGeometry,
): ClosestPointPair | null {
    switch (geometryA.kind) {
        case MeasureDistancePrimitiveKind.Point:
            return closestPointToGeometry(geometryA.position, geometryB);
        case MeasureDistancePrimitiveKind.Segment:
            return closestSegmentToGeometry(geometryA.start, geometryA.end, geometryB);
        case MeasureDistancePrimitiveKind.InfiniteLine:
            return closestInfiniteLineToGeometry(geometryA.origin, geometryA.direction, geometryB);
        case MeasureDistancePrimitiveKind.Polyline:
            return closestPolylineToGeometry(geometryA.points, geometryB);
        case MeasureDistancePrimitiveKind.Plane:
            return closestPlaneToGeometry(geometryA.point, geometryA.normal, geometryB);
        case MeasureDistancePrimitiveKind.MeshFace:
            return closestMeshFaceToGeometry(geometryA.face, geometryB);
        default:
            return null;
    }
}

/**
 * 求点到任意几何体的最近点对。
 *
 * @param point 查询点。
 * @param geometry 目标几何体。
 * @returns 最近点对（查询点侧为 pointA）。
 */
function closestPointToGeometry(
    point: THREE.Vector3,
    geometry: MeasureDistanceGeometry,
): ClosestPointPair | null {
    switch (geometry.kind) {
        case MeasureDistancePrimitiveKind.Point:
            return { pointA: point.clone(), pointB: geometry.position.clone() };
        case MeasureDistancePrimitiveKind.Segment: {
            const onSegment = closestPointOnSegment(point, geometry.start, geometry.end);
            return { pointA: point.clone(), pointB: onSegment };
        }
        case MeasureDistancePrimitiveKind.InfiniteLine: {
            const onLine = closestPointOnInfiniteLine(point, geometry.origin, geometry.direction);
            return { pointA: point.clone(), pointB: onLine };
        }
        case MeasureDistancePrimitiveKind.Polyline: {
            const onPolyline = closestPointOnPolyline(point, geometry.points);
            if (!onPolyline) {
                return null;
            }
            return { pointA: point.clone(), pointB: onPolyline };
        }
        case MeasureDistancePrimitiveKind.Plane: {
            const onPlane = projectPointOntoPlane(point, geometry.point, geometry.normal);
            return { pointA: point.clone(), pointB: onPlane };
        }
        case MeasureDistancePrimitiveKind.MeshFace: {
            const closestPoint = closestPointOnMeshFaceForMeasure(geometry.face, point);
            if (!closestPoint) {
                return null;
            }
            return { pointA: point.clone(), pointB: closestPoint };
        }
        default:
            return null;
    }
}

/**
 * 求线段到有边界平面面的最近点对。
 *
 * 设计说明：
 * 1. 模型拓扑面为有限面域，参考点必须落在面三角网格边界内；
 * 2. 候选包含「线段采样点 → 面投影」与「面顶点 → 线段最近点」，不按无限平面延伸。
 *
 * @param start 线段起点。
 * @param end 线段终点。
 * @param face 面代理。
 * @returns 最近点对（点 A 在线段上，点 B 在面上）；无法求解时返回 null。
 */
function closestPointsBetweenSegmentAndMeshFace(
    start: THREE.Vector3,
    end: THREE.Vector3,
    face: FeatureFace,
): ClosestPointPair | null {
    let best: ClosestPointPair | null = null;
    let bestDistanceSq = Number.POSITIVE_INFINITY;

    const consider = (pointOnSegment: THREE.Vector3, pointOnFace: THREE.Vector3): void => {
        const distanceSq = pointOnSegment.distanceToSquared(pointOnFace);
        if (distanceSq >= bestDistanceSq) {
            return;
        }
        bestDistanceSq = distanceSq;
        best = {
            pointA: pointOnSegment.clone(),
            pointB: pointOnFace.clone(),
        };
    };

    sampleSegmentPoints(start, end).forEach((sample) => {
        const closestPoint = closestPointOnMeshFaceForMeasure(face, sample);
        if (closestPoint) {
            const onSegment = closestPointOnSegment(closestPoint, start, end);
            consider(onSegment, closestPoint);
        }
    });

    if (resolveBoundedPlanarFaceWorldPlane(face)) {
        collectFeatureFaceWorldVertices(face).forEach((vertex) => {
            consider(closestPointOnSegment(vertex, start, end), vertex);
        });
    }

    return best;
}

/**
 * 求无限直线到有边界平面面的最近点对。
 *
 * 设计说明：与 {@link closestPointsBetweenSegmentAndMeshFace} 一致，在面边界内取最近点。
 *
 * @param origin 直线上一点。
 * @param direction 单位方向。
 * @param face 面代理。
 * @returns 最近点对（点 A 在直线上，点 B 在面上）；无法求解时返回 null。
 */
function closestPointsBetweenInfiniteLineAndMeshFace(
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    face: FeatureFace,
): ClosestPointPair | null {
    let best: ClosestPointPair | null = null;
    let bestDistanceSq = Number.POSITIVE_INFINITY;

    const consider = (pointOnLine: THREE.Vector3, pointOnFace: THREE.Vector3): void => {
        const distanceSq = pointOnLine.distanceToSquared(pointOnFace);
        if (distanceSq >= bestDistanceSq) {
            return;
        }
        bestDistanceSq = distanceSq;
        best = {
            pointA: pointOnLine.clone(),
            pointB: pointOnFace.clone(),
        };
    };

    if (resolveBoundedPlanarFaceWorldPlane(face)) {
        collectFeatureFaceWorldVertices(face).forEach((vertex) => {
            consider(closestPointOnInfiniteLine(vertex, origin, direction), vertex);
        });
    }

    sampleInfiniteLinePointsNearBoundedFace(origin, direction, face).forEach((sample) => {
        const closestPoint = closestPointOnMeshFaceForMeasure(face, sample);
        if (closestPoint) {
            consider(
                closestPointOnInfiniteLine(closestPoint, origin, direction),
                closestPoint,
            );
        }
    });

    return best;
}

/**
 * 求线段到任意几何体的最近点对。
 *
 * @param start 线段起点。
 * @param end 线段终点。
 * @param geometry 目标几何体。
 * @returns 最近点对。
 */
function closestSegmentToGeometry(
    start: THREE.Vector3,
    end: THREE.Vector3,
    geometry: MeasureDistanceGeometry,
): ClosestPointPair | null {
    let best: ClosestPointPair | null = null;
    let bestDistanceSq = Number.POSITIVE_INFINITY;

    const consider = (pointOnSegment: THREE.Vector3, pointOnTarget: THREE.Vector3): void => {
        const distanceSq = pointOnSegment.distanceToSquared(pointOnTarget);
        if (distanceSq >= bestDistanceSq) {
            return;
        }
        bestDistanceSq = distanceSq;
        best = {
            pointA: pointOnSegment.clone(),
            pointB: pointOnTarget.clone(),
        };
    };

    switch (geometry.kind) {
        case MeasureDistancePrimitiveKind.Point: {
            const onSegment = closestPointOnSegment(geometry.position, start, end);
            consider(onSegment, geometry.position);
            return best;
        }
        case MeasureDistancePrimitiveKind.Segment: {
            const pair = closestPointsBetweenSegments(start, end, geometry.start, geometry.end);
            return pair ? { pointA: pair.pointA, pointB: pair.pointB } : null;
        }
        case MeasureDistancePrimitiveKind.InfiniteLine: {
            const pair = closestPointsBetweenSegmentAndInfiniteLine(start, end, geometry.origin, geometry.direction);
            return pair ? { pointA: pair.pointA, pointB: pair.pointB } : null;
        }
        case MeasureDistancePrimitiveKind.Polyline: {
            for (let index = 1; index < geometry.points.length; index += 1) {
                const segStart = geometry.points[index - 1]!;
                const segEnd = geometry.points[index]!;
                const pair = closestPointsBetweenSegments(start, end, segStart, segEnd);
                if (pair) {
                    consider(pair.pointA, pair.pointB);
                }
            }
            return best;
        }
        case MeasureDistancePrimitiveKind.Plane: {
            considerEndpointsAndInteriorOnPlane(start, end, geometry.point, geometry.normal, consider);
            return best;
        }
        case MeasureDistancePrimitiveKind.MeshFace: {
            return closestPointsBetweenSegmentAndMeshFace(start, end, geometry.face);
        }
        default:
            return null;
    }
}

/**
 * 求无限直线到任意几何体的最近点对。
 *
 * @param origin 直线上一点。
 * @param direction 单位方向。
 * @param geometry 目标几何体。
 * @returns 最近点对。
 */
function closestInfiniteLineToGeometry(
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    geometry: MeasureDistanceGeometry,
): ClosestPointPair | null {
    switch (geometry.kind) {
        case MeasureDistancePrimitiveKind.Point: {
            const onLine = closestPointOnInfiniteLine(geometry.position, origin, direction);
            return { pointA: onLine, pointB: geometry.position.clone() };
        }
        case MeasureDistancePrimitiveKind.Segment: {
            const pair = closestPointsBetweenSegmentAndInfiniteLine(
                geometry.start,
                geometry.end,
                origin,
                direction,
            );
            return pair ? { pointA: pair.pointB, pointB: pair.pointA } : null;
        }
        case MeasureDistancePrimitiveKind.InfiniteLine: {
            const pair = closestPointsBetweenInfiniteLines(origin, direction, geometry.origin, geometry.direction);
            return pair ? { pointA: pair.pointA, pointB: pair.pointB } : null;
        }
        case MeasureDistancePrimitiveKind.Polyline: {
            let best: ClosestPointPair | null = null;
            let bestDistanceSq = Number.POSITIVE_INFINITY;
            for (let index = 1; index < geometry.points.length; index += 1) {
                const pair = closestPointsBetweenSegmentAndInfiniteLine(
                    geometry.points[index - 1]!,
                    geometry.points[index]!,
                    origin,
                    direction,
                );
                if (!pair) {
                    continue;
                }
                const distanceSq = pair.pointA.distanceToSquared(pair.pointB);
                if (distanceSq < bestDistanceSq) {
                    bestDistanceSq = distanceSq;
                    best = { pointA: pair.pointB, pointB: pair.pointA };
                }
            }
            return best;
        }
        case MeasureDistancePrimitiveKind.Plane: {
            return closestPlaneToInfiniteLine(geometry.point, geometry.normal, origin, direction);
        }
        case MeasureDistancePrimitiveKind.MeshFace: {
            return closestPointsBetweenInfiniteLineAndMeshFace(origin, direction, geometry.face);
        }
        default:
            return null;
    }
}

/**
 * 求折线到任意几何体的最近点对。
 *
 * @param points 折线点列。
 * @param geometry 目标几何体。
 * @returns 最近点对。
 */
function closestPolylineToGeometry(
    points: readonly THREE.Vector3[],
    geometry: MeasureDistanceGeometry,
): ClosestPointPair | null {
    let best: ClosestPointPair | null = null;
    let bestDistanceSq = Number.POSITIVE_INFINITY;

    for (let index = 1; index < points.length; index += 1) {
        const segmentResult = closestSegmentToGeometry(points[index - 1]!, points[index]!, geometry);
        if (!segmentResult) {
            continue;
        }
        const distanceSq = segmentResult.pointA.distanceToSquared(segmentResult.pointB);
        if (distanceSq < bestDistanceSq) {
            bestDistanceSq = distanceSq;
            best = segmentResult;
        }
    }

    return best;
}

/**
 * 求平面到任意几何体的最近点对。
 *
 * @param planePoint 平面上一点。
 * @param planeNormal 单位法向。
 * @param geometry 目标几何体。
 * @returns 最近点对。
 */
function closestPlaneToGeometry(
    planePoint: THREE.Vector3,
    planeNormal: THREE.Vector3,
    geometry: MeasureDistanceGeometry,
): ClosestPointPair | null {
    switch (geometry.kind) {
        case MeasureDistancePrimitiveKind.Point: {
            const onPlane = projectPointOntoPlane(geometry.position, planePoint, planeNormal);
            return { pointA: onPlane, pointB: geometry.position.clone() };
        }
        case MeasureDistancePrimitiveKind.Segment: {
            const reversed = closestSegmentToGeometry(geometry.start, geometry.end, {
                kind: MeasureDistancePrimitiveKind.Plane,
                point: planePoint,
                normal: planeNormal,
            });
            return reversed ? { pointA: reversed.pointB, pointB: reversed.pointA } : null;
        }
        case MeasureDistancePrimitiveKind.InfiniteLine: {
            return closestPlaneToInfiniteLine(planePoint, planeNormal, geometry.origin, geometry.direction);
        }
        case MeasureDistancePrimitiveKind.Polyline: {
            let best: ClosestPointPair | null = null;
            let bestDistanceSq = Number.POSITIVE_INFINITY;
            geometry.points.forEach((point) => {
                const onPlane = projectPointOntoPlane(point, planePoint, planeNormal);
                const distanceSq = onPlane.distanceToSquared(point);
                if (distanceSq < bestDistanceSq) {
                    bestDistanceSq = distanceSq;
                    best = { pointA: onPlane, pointB: point.clone() };
                }
            });
            return best;
        }
        case MeasureDistancePrimitiveKind.Plane: {
            return closestPointsBetweenPlanes(planePoint, planeNormal, geometry.point, geometry.normal);
        }
        case MeasureDistancePrimitiveKind.MeshFace: {
            const boundedPlane = resolveBoundedPlanarFaceWorldPlane(geometry.face);
            if (boundedPlane) {
                return closestPlaneToBoundedPlanarFace(
                    planePoint,
                    planeNormal,
                    geometry.face,
                    boundedPlane,
                );
            }

            const centroid = geometry.face.getWorldPoint();
            const onPlane = projectPointOntoPlane(centroid, planePoint, planeNormal);
            const closestPoint = closestPointOnMeshFaceForMeasure(geometry.face, onPlane);
            if (!closestPoint) {
                return { pointA: onPlane, pointB: centroid.clone() };
            }
            const refinedOnPlane = projectPointOntoPlane(closestPoint, planePoint, planeNormal);
            return { pointA: refinedOnPlane, pointB: closestPoint };
        }
        default:
            return null;
    }
}

/**
 * 求网格面到任意几何体的最近点对。
 *
 * @param face 面代理。
 * @param geometry 目标几何体。
 * @returns 最近点对。
 */
function closestMeshFaceToGeometry(
    face: FeatureFace,
    geometry: MeasureDistanceGeometry,
): ClosestPointPair | null {
    if (geometry.kind === MeasureDistancePrimitiveKind.MeshFace) {
        const pair = computeClosestPointsBetweenMeshFaces(face, geometry.face);
        return pair ? { pointA: pair.pointOnA, pointB: pair.pointOnB } : null;
    }

    if (geometry.kind === MeasureDistancePrimitiveKind.Plane) {
        const boundedPlane = resolveBoundedPlanarFaceWorldPlane(face);
        if (boundedPlane) {
            const pair = closestPlaneToBoundedPlanarFace(
                geometry.point,
                geometry.normal,
                face,
                boundedPlane,
            );
            return pair ? { pointA: pair.pointB, pointB: pair.pointA } : null;
        }
    }

    const centroid = face.getWorldPoint();
    const reversed = closestPointToGeometry(centroid, geometry);
    if (!reversed) {
        return null;
    }

    const projection = closestPointOnMeshFaceForMeasure(face, reversed.pointB);
    if (!projection) {
        return { pointA: centroid.clone(), pointB: reversed.pointB.clone() };
    }

    const refined = closestPointToGeometry(projection, geometry);
    if (!refined) {
        return { pointA: projection.clone(), pointB: reversed.pointB.clone() };
    }

    const backProjection = closestPointOnMeshFaceForMeasure(face, refined.pointB);
    return {
        pointA: (backProjection ?? projection).clone(),
        pointB: refined.pointB.clone(),
    };
}

/**
 * 求平面与无限直线的最近点对。
 *
 * @param planePoint 平面上一点。
 * @param planeNormal 单位法向。
 * @param lineOrigin 直线上一点。
 * @param lineDirection 直线单位方向。
 * @returns 最近点对。
 */
function closestPlaneToInfiniteLine(
    planePoint: THREE.Vector3,
    planeNormal: THREE.Vector3,
    lineOrigin: THREE.Vector3,
    lineDirection: THREE.Vector3,
): ClosestPointPair | null {
    const denominator = planeNormal.dot(lineDirection);
    if (Math.abs(denominator) <= MEASURE_CLOSEST_DISTANCE_EPSILON) {
        const onPlane = projectPointOntoPlane(lineOrigin, planePoint, planeNormal);
        const onLine = closestPointOnInfiniteLine(onPlane, lineOrigin, lineDirection);
        return { pointA: onPlane, pointB: onLine };
    }

    const distanceAlongNormal = planePoint.clone().sub(lineOrigin).dot(planeNormal) / denominator;
    const onLine = lineOrigin.clone().addScaledVector(lineDirection, distanceAlongNormal);
    const onPlane = projectPointOntoPlane(onLine, planePoint, planeNormal);
    return { pointA: onPlane, pointB: onLine };
}

/**
 * 求两平面之间的最近点对。
 *
 * @param pointA 平面 A 上一点。
 * @param normalA 平面 A 单位法向。
 * @param pointB 平面 B 上一点。
 * @param normalB 平面 B 单位法向。
 * @returns 最近点对。
 */
function closestPointsBetweenPlanes(
    pointA: THREE.Vector3,
    normalA: THREE.Vector3,
    pointB: THREE.Vector3,
    normalB: THREE.Vector3,
): ClosestPointPair {
    const parallel = Math.abs(normalA.dot(normalB)) > 1 - 1e-6;
    if (!parallel) {
        const intersectionPoint = pointA.clone();
        return {
            pointA: intersectionPoint,
            pointB: intersectionPoint,
        };
    }

    const signedDistance = pointB.clone().sub(pointA).dot(normalA);
    const pointOnB = pointA.clone().addScaledVector(normalA, signedDistance);
    return {
        pointA: pointA.clone(),
        pointB: pointOnB,
    };
}

/**
 * 求无限平面到有边界平面面的最近点对。
 *
 * 说明：基准面按无限平面处理，模型普通平面必须保持有界面域语义；非平行时若交线穿过面域，距离为 0。
 *
 * @param planePoint 无限平面上一点。
 * @param planeNormal 无限平面单位法向。
 * @param face 有边界平面面。
 * @param boundedPlane 有边界平面所在世界平面。
 * @returns 最近点对（pointA 在无限平面上，pointB 在有界面上）。
 */
function closestPlaneToBoundedPlanarFace(
    planePoint: THREE.Vector3,
    planeNormal: THREE.Vector3,
    face: FeatureFace,
    boundedPlane: { point: THREE.Vector3; normal: THREE.Vector3 },
): ClosestPointPair | null {
    if (areMeasureNormalsParallel(planeNormal, boundedPlane.normal)) {
        // SolidWorks：无限基准面与平行模型平面面之间报告 Normal Distance。
        return closestPointsBetweenPlanes(
            planePoint,
            planeNormal,
            boundedPlane.point,
            boundedPlane.normal,
        );
    }

    const intersectionPoint = tryResolvePlaneBoundedFaceIntersectionPoint(
        planePoint,
        planeNormal,
        face,
    );
    if (intersectionPoint) {
        return {
            pointA: intersectionPoint.clone(),
            pointB: intersectionPoint.clone(),
        };
    }

    return closestPlaneToBoundedPlanarFaceBoundary(planePoint, planeNormal, face);
}

/**
 * 尝试解析无限平面与有边界平面面的交点。
 *
 * @param planePoint 无限平面上一点。
 * @param planeNormal 无限平面单位法向。
 * @param face 有边界平面面。
 * @returns 任意交点；无交点时返回 null。
 */
function tryResolvePlaneBoundedFaceIntersectionPoint(
    planePoint: THREE.Vector3,
    planeNormal: THREE.Vector3,
    face: FeatureFace,
): THREE.Vector3 | null {
    let intersection: THREE.Vector3 | null = null;
    forEachFeatureFaceTriangleWorldPoints(face, (first, second, third) => {
        if (intersection) {
            return;
        }
        intersection = resolvePlaneTriangleIntersectionPoint(
            planePoint,
            planeNormal,
            first,
            second,
            third,
        );
    });
    return intersection;
}

/**
 * 解析无限平面与单个三角形的任意交点。
 *
 * @param planePoint 无限平面上一点。
 * @param planeNormal 无限平面单位法向。
 * @param first 三角形顶点 A。
 * @param second 三角形顶点 B。
 * @param third 三角形顶点 C。
 * @returns 任意交点；无交点时返回 null。
 */
function resolvePlaneTriangleIntersectionPoint(
    planePoint: THREE.Vector3,
    planeNormal: THREE.Vector3,
    first: THREE.Vector3,
    second: THREE.Vector3,
    third: THREE.Vector3,
): THREE.Vector3 | null {
    return resolvePlaneSegmentIntersectionPoint(planePoint, planeNormal, first, second)
        ?? resolvePlaneSegmentIntersectionPoint(planePoint, planeNormal, second, third)
        ?? resolvePlaneSegmentIntersectionPoint(planePoint, planeNormal, third, first);
}

/**
 * 解析无限平面与线段的交点。
 *
 * @param planePoint 无限平面上一点。
 * @param planeNormal 无限平面单位法向。
 * @param start 线段起点。
 * @param end 线段终点。
 * @returns 交点；未相交时返回 null。
 */
function resolvePlaneSegmentIntersectionPoint(
    planePoint: THREE.Vector3,
    planeNormal: THREE.Vector3,
    start: THREE.Vector3,
    end: THREE.Vector3,
): THREE.Vector3 | null {
    const signedStart = start.clone().sub(planePoint).dot(planeNormal);
    const signedEnd = end.clone().sub(planePoint).dot(planeNormal);
    if (Math.abs(signedStart) <= MEASURE_CLOSEST_DISTANCE_EPSILON) {
        return start.clone();
    }
    if (Math.abs(signedEnd) <= MEASURE_CLOSEST_DISTANCE_EPSILON) {
        return end.clone();
    }
    if (signedStart * signedEnd > 0) {
        return null;
    }

    const denominator = signedStart - signedEnd;
    if (Math.abs(denominator) <= MEASURE_CLOSEST_DISTANCE_EPSILON) {
        return null;
    }

    const t = signedStart / denominator;
    if (t < 0 || t > 1) {
        return null;
    }
    return start.clone().lerp(end, t);
}

/**
 * 求无限平面到有边界平面面域边界的最近点对。
 *
 * @param planePoint 无限平面上一点。
 * @param planeNormal 无限平面单位法向。
 * @param face 有边界平面面。
 * @returns 最近点对；无法读取面顶点时返回 null。
 */
function closestPlaneToBoundedPlanarFaceBoundary(
    planePoint: THREE.Vector3,
    planeNormal: THREE.Vector3,
    face: FeatureFace,
): ClosestPointPair | null {
    let best: ClosestPointPair | null = null;
    let bestDistanceSq = Number.POSITIVE_INFINITY;

    collectFeatureFaceWorldVertices(face).forEach((vertex) => {
        const onPlane = projectPointOntoPlane(vertex, planePoint, planeNormal);
        const distanceSq = vertex.distanceToSquared(onPlane);
        if (distanceSq >= bestDistanceSq) {
            return;
        }
        bestDistanceSq = distanceSq;
        best = {
            pointA: onPlane,
            pointB: vertex.clone(),
        };
    });

    return best;
}

/**
 * 遍历特征面三角形世界坐标点。
 *
 * @param face 特征面。
 * @param visit 三角形访问回调。
 */
function forEachFeatureFaceTriangleWorldPoints(
    face: FeatureFace,
    visit: (first: THREE.Vector3, second: THREE.Vector3, third: THREE.Vector3) => void,
): void {
    const positionAttribute = face.geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute)) {
        return;
    }

    face.updateWorldMatrix(true, false);
    const indexAttribute = face.geometry.getIndex();
    const vertexCount = indexAttribute instanceof THREE.BufferAttribute
        ? indexAttribute.count
        : positionAttribute.count;

    for (let cursor = 0; cursor + 2 < vertexCount; cursor += 3) {
        const first = readFeatureFaceTriangleWorldPoint(face, positionAttribute, indexAttribute, cursor);
        const second = readFeatureFaceTriangleWorldPoint(face, positionAttribute, indexAttribute, cursor + 1);
        const third = readFeatureFaceTriangleWorldPoint(face, positionAttribute, indexAttribute, cursor + 2);
        if (!first || !second || !third) {
            continue;
        }
        visit(first, second, third);
    }
}

/**
 * 读取特征面三角形顶点世界坐标。
 *
 * @param face 特征面。
 * @param positionAttribute 顶点位置属性。
 * @param indexAttribute 顶点索引属性。
 * @param cursor 三角形游标。
 * @returns 世界坐标点；索引无效时返回 null。
 */
function readFeatureFaceTriangleWorldPoint(
    face: FeatureFace,
    positionAttribute: THREE.BufferAttribute,
    indexAttribute: THREE.BufferAttribute | null,
    cursor: number,
): THREE.Vector3 | null {
    const vertexIndex = indexAttribute instanceof THREE.BufferAttribute
        ? Number(indexAttribute.getX(cursor))
        : cursor;
    if (vertexIndex < 0 || vertexIndex >= positionAttribute.count) {
        return null;
    }
    return new THREE.Vector3(
        positionAttribute.getX(vertexIndex),
        positionAttribute.getY(vertexIndex),
        positionAttribute.getZ(vertexIndex),
    ).applyMatrix4(face.matrixWorld);
}

/**
 * 将点投影到无限延伸平面。
 *
 * @param point 待投影点。
 * @param planePoint 平面上一点。
 * @param planeNormal 单位法向。
 * @returns 投影点。
 */
function projectPointOntoPlane(
    point: THREE.Vector3,
    planePoint: THREE.Vector3,
    planeNormal: THREE.Vector3,
): THREE.Vector3 {
    const offset = point.clone().sub(planePoint);
    const distance = offset.dot(planeNormal);
    return point.clone().addScaledVector(planeNormal, -distance);
}

/**
 * 求点到线段的最近点。
 *
 * @param point 查询点。
 * @param start 线段起点。
 * @param end 线段终点。
 * @returns 线段上的最近点。
 */
function closestPointOnSegment(
    point: THREE.Vector3,
    start: THREE.Vector3,
    end: THREE.Vector3,
): THREE.Vector3 {
    const ab = end.clone().sub(start);
    const ap = point.clone().sub(start);
    const abLenSq = ab.lengthSq();
    if (abLenSq <= MEASURE_CLOSEST_DISTANCE_EPSILON) {
        return start.clone();
    }
    const t = Math.max(0, Math.min(1, ap.dot(ab) / abLenSq));
    return start.clone().addScaledVector(ab, t);
}

/**
 * 求点到无限直线的最近点。
 *
 * @param point 查询点。
 * @param origin 直线上一点。
 * @param direction 单位方向。
 * @returns 直线上的最近点。
 */
function closestPointOnInfiniteLine(
    point: THREE.Vector3,
    origin: THREE.Vector3,
    direction: THREE.Vector3,
): THREE.Vector3 {
    const offset = point.clone().sub(origin);
    const t = offset.dot(direction);
    return origin.clone().addScaledVector(direction, t);
}

/**
 * 求点到折线的最近点。
 *
 * @param point 查询点。
 * @param points 折线点列。
 * @returns 折线上的最近点；无效时返回 null。
 */
function closestPointOnPolyline(
    point: THREE.Vector3,
    points: readonly THREE.Vector3[],
): THREE.Vector3 | null {
    if (points.length < 2) {
        return null;
    }

    let bestPoint: THREE.Vector3 | null = null;
    let bestDistanceSq = Number.POSITIVE_INFINITY;
    for (let index = 1; index < points.length; index += 1) {
        const candidate = closestPointOnSegment(point, points[index - 1]!, points[index]!);
        const distanceSq = candidate.distanceToSquared(point);
        if (distanceSq < bestDistanceSq) {
            bestDistanceSq = distanceSq;
            bestPoint = candidate;
        }
    }
    return bestPoint;
}

/**
 * 求两线段之间的最近点对。
 *
 * @param startA 线段 A 起点。
 * @param endA 线段 A 终点。
 * @param startB 线段 B 起点。
 * @param endB 线段 B 终点。
 * @returns 最近点对。
 */
function closestPointsBetweenSegments(
    startA: THREE.Vector3,
    endA: THREE.Vector3,
    startB: THREE.Vector3,
    endB: THREE.Vector3,
): ClosestPointPair | null {
    const directionA = endA.clone().sub(startA);
    const directionB = endB.clone().sub(startB);
    const originDiff = startA.clone().sub(startB);

    const a = directionA.dot(directionA);
    const b = directionA.dot(directionB);
    const c = directionB.dot(directionB);
    const d = directionA.dot(originDiff);
    const e = directionB.dot(originDiff);
    const denominator = a * c - b * b;

    let paramA = 0;
    let paramB = 0;

    if (denominator <= MEASURE_CLOSEST_DISTANCE_EPSILON) {
        paramA = 0;
        paramB = c > MEASURE_CLOSEST_DISTANCE_EPSILON ? e / c : 0;
    } else {
        paramA = (b * e - c * d) / denominator;
        paramB = (a * e - b * d) / denominator;
    }

    paramA = clamp01(paramA);
    paramB = clamp01(paramB);

    const pointA = startA.clone().addScaledVector(directionA, paramA);
    const pointB = startB.clone().addScaledVector(directionB, paramB);
    return { pointA, pointB };
}

/**
 * 求线段与无限直线之间的最近点对。
 *
 * @param segStart 线段起点。
 * @param segEnd 线段终点。
 * @param lineOrigin 直线上一点。
 * @param lineDirection 直线单位方向。
 * @returns 最近点对（pointA 在线段上，pointB 在直线上）。
 */
function closestPointsBetweenSegmentAndInfiniteLine(
    segStart: THREE.Vector3,
    segEnd: THREE.Vector3,
    lineOrigin: THREE.Vector3,
    lineDirection: THREE.Vector3,
): ClosestPointPair | null {
    const segDirection = segEnd.clone().sub(segStart);
    const segLenSq = segDirection.lengthSq();
    const cross = segDirection.clone().cross(lineDirection);
    const crossLenSq = cross.lengthSq();

    if (crossLenSq <= MEASURE_CLOSEST_DISTANCE_EPSILON) {
        const onLineFromStart = closestPointOnInfiniteLine(segStart, lineOrigin, lineDirection);
        const onLineFromEnd = closestPointOnInfiniteLine(segEnd, lineOrigin, lineDirection);
        const startPair = { pointA: segStart.clone(), pointB: onLineFromStart };
        const endPair = { pointA: segEnd.clone(), pointB: onLineFromEnd };
        if (startPair.pointA.distanceToSquared(startPair.pointB) <= endPair.pointA.distanceToSquared(endPair.pointB)) {
            return startPair;
        }
        return endPair;
    }

    const matrix = [
        [segLenSq, -segDirection.dot(lineDirection)],
        [-segDirection.dot(lineDirection), 1],
    ];
    const rhs = [
        -segStart.clone().sub(lineOrigin).dot(segDirection),
        -segStart.clone().sub(lineOrigin).dot(lineDirection),
    ];
    const det = matrix[0]![0]! * matrix[1]![1]! - matrix[0]![1]! * matrix[1]![0]!;
    let paramSeg = 0;
    let paramLine = 0;
    if (Math.abs(det) > MEASURE_CLOSEST_DISTANCE_EPSILON) {
        paramSeg = clamp01((rhs[0]! * matrix[1]![1]! - rhs[1]! * matrix[0]![1]!) / det);
        paramLine = (matrix[0]![0]! * rhs[1]! - matrix[1]![0]! * rhs[0]!) / det;
    }

    const pointA = segStart.clone().addScaledVector(segDirection, paramSeg);
    const pointB = lineOrigin.clone().addScaledVector(lineDirection, paramLine);
    return { pointA, pointB };
}

/**
 * 求两无限直线之间的最近点对。
 *
 * @param originA 直线 A 上一点。
 * @param directionA 直线 A 单位方向。
 * @param originB 直线 B 上一点。
 * @param directionB 直线 B 单位方向。
 * @returns 最近点对。
 */
function closestPointsBetweenInfiniteLines(
    originA: THREE.Vector3,
    directionA: THREE.Vector3,
    originB: THREE.Vector3,
    directionB: THREE.Vector3,
): ClosestPointPair | null {
    const cross = directionA.clone().cross(directionB);
    const crossLenSq = cross.lengthSq();
    if (crossLenSq <= MEASURE_CLOSEST_DISTANCE_EPSILON) {
        const onB = closestPointOnInfiniteLine(originA, originB, directionB);
        return { pointA: originA.clone(), pointB: onB };
    }

    const originDiff = originA.clone().sub(originB);
    const paramA = originDiff.clone().cross(directionB).dot(cross) / crossLenSq;
    const paramB = originDiff.clone().cross(directionA).dot(cross) / crossLenSq;
    const pointA = originA.clone().addScaledVector(directionA, paramA);
    const pointB = originB.clone().addScaledVector(directionB, paramB);
    return { pointA, pointB };
}

/**
 * 在线段投影到平面时，考虑端点与线段内部的候选最近点。
 *
 * @param start 线段起点。
 * @param end 线段终点。
 * @param planePoint 平面上一点。
 * @param planeNormal 单位法向。
 * @param consider 候选回调。
 */
function considerEndpointsAndInteriorOnPlane(
    start: THREE.Vector3,
    end: THREE.Vector3,
    planePoint: THREE.Vector3,
    planeNormal: THREE.Vector3,
    consider: (pointOnSegment: THREE.Vector3, pointOnPlane: THREE.Vector3) => void,
): void {
    const projectedStart = projectPointOntoPlane(start, planePoint, planeNormal);
    const projectedEnd = projectPointOntoPlane(end, planePoint, planeNormal);
    consider(start, projectedStart);
    consider(end, projectedEnd);

    const direction = end.clone().sub(start);
    if (direction.lengthSq() <= MEASURE_CLOSEST_DISTANCE_EPSILON) {
        return;
    }

    const denominator = direction.dot(planeNormal);
    if (Math.abs(denominator) <= MEASURE_CLOSEST_DISTANCE_EPSILON) {
        return;
    }

    const t = planePoint.clone().sub(start).dot(planeNormal) / denominator;
    if (t < 0 || t > 1) {
        return;
    }
    const intersection = start.clone().addScaledVector(direction, t);
    consider(intersection, intersection.clone());
}

/**
 * 沿无限直线在面投影参数域附近采样。
 *
 * @param origin 直线上一点。
 * @param direction 单位方向。
 * @param face 有边界平面面。
 * @returns 直线采样点列。
 */
function sampleInfiniteLinePointsNearBoundedFace(
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    face: FeatureFace,
): THREE.Vector3[] {
    const tValues: number[] = [];
    collectFeatureFaceWorldVertices(face).forEach((vertex) => {
        tValues.push(vertex.clone().sub(origin).dot(direction));
    });
    tValues.push(face.getWorldPoint().clone().sub(origin).dot(direction));

    if (tValues.length <= 0) {
        return [origin.clone()];
    }

    const minT = Math.min(...tValues);
    const maxT = Math.max(...tValues);
    const span = Math.max(maxT - minT, MEASURE_CLOSEST_DISTANCE_EPSILON);
    const margin = span * 0.25;

    return [
        origin.clone().addScaledVector(direction, minT - margin),
        origin.clone().addScaledVector(direction, minT),
        origin.clone().addScaledVector(direction, (minT + maxT) * 0.5),
        origin.clone().addScaledVector(direction, maxT),
        origin.clone().addScaledVector(direction, maxT + margin),
    ];
}

/**
 * 沿线段均匀采样。
 *
 * @param start 线段起点。
 * @param end 线段终点。
 * @returns 采样点列。
 */
function sampleSegmentPoints(start: THREE.Vector3, end: THREE.Vector3): THREE.Vector3[] {
    return [
        start.clone(),
        start.clone().lerp(end, 0.25),
        start.clone().lerp(end, 0.5),
        start.clone().lerp(end, 0.75),
        end.clone(),
    ];
}

/**
 * 将参数夹紧到 [0, 1]。
 *
 * @param value 输入值。
 * @returns 夹紧后的值。
 */
function clamp01(value: number): number {
    return Math.max(0, Math.min(1, value));
}
